/**
 * Endpoints del **turno del operador** (`/api/turno*`).
 *
 * Los listados van acotados al turno abierto del usuario: el backend resuelve cuál
 * es (no se manda un `turno_id` desde el navegador, para que nadie consulte el turno
 * de otro) y devuelve `turno_id` en la respuesta.
 *
 * El cuadre, las bóvedas, los gastos y la corrección de cobros se resuelven con los
 * endpoints de `/api/liquidaciones_*`, que ya usa *Reportes · Turnos*.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  BoleteoRespuesta,
  BoleteoYCierreRespuesta,
  CreditoDelTurno,
  DocumentoDelTurno,
  EstadoBombasRespuesta,
  HistorialTurnosRespuesta,
  PaginaDelTurno,
  PosCatalogosRespuesta,
  PosCalibracionRespuesta,
  PosConsumoInternoRespuesta,
  PosConsumoInternoSolicitud,
  PosComprobanteSolicitud,
  PosComprobanteRespuesta,
  LineasCreditoRespuesta,
  DocumentoConsultado,
  PosVentaCreditoSolicitud,
  PosVentaCreditoRespuesta,
  TurnoActualRespuesta,
  UsuarioDelTurno,
} from './turno.types';
import type { ValidacionCodigo } from '@/features/calibracion/calibracion.types';

/**
 * Tiempo máximo del **cierre del turno** y del boleteo manual.
 *
 * Son más lentos que una escritura normal porque antes de cerrar la API pide al servicio POS
 * que emita los comprobantes de las ventas libres (puede generar varios documentos).
 */
const CIERRE_TIMEOUT_MS = 55_000;

const CADENA = (parametros: Record<string, string | number | undefined>) => {
  const busqueda = new URLSearchParams();

  for (const [clave, valor] of Object.entries(parametros)) {
    if (valor !== undefined && valor !== '') busqueda.set(clave, String(valor));
  }

  const cadena = busqueda.toString();

  return cadena === '' ? '' : `?${cadena}`;
};

export const turnoKeys = {
  all: ['turno'] as const,
  actual: ['turno', 'actual'] as const,
  bombasEstado: ['turno', 'bombas', 'estado'] as const,
  historial: (fecha: string) => ['turno', 'historial', fecha] as const,
  usuarios: ['turno', 'usuarios'] as const,
  documentos: (filtros: FiltrosListado) => ['turno', 'documentos', filtros] as const,
  notas: (filtros: FiltrosListado) => ['turno', 'notas', filtros] as const,
  creditos: (filtros: FiltrosListado) => ['turno', 'creditos', filtros] as const,
  posCatalogos: (buscar: string, clienteId?: number, tipoDocumento?: string, placa?: string) =>
    ['turno', 'pos', 'catalogos', buscar, clienteId, tipoDocumento, placa] as const,
};

/** Filtros de los listados del turno. */
export interface FiltrosListado {
  page: number;
  pageSize: number;
  buscar: string;
}

export const turnoApi = {
  actual: (signal?: AbortSignal): Promise<TurnoActualRespuesta> =>
    apiClient.get<TurnoActualRespuesta>('/api/turno/actual', { signal }),

  estadoBombas: (signal?: AbortSignal): Promise<EstadoBombasRespuesta> =>
    apiClient.get<EstadoBombasRespuesta>('/api/turno/bombas/estado', { signal }),

  cambiarOperacionBomba: (bomba: number, estado: 'On' | 'Off') =>
    apiClient.post<{ bomba: number; estado: 'On' | 'Off'; mensaje: string }>(
      '/api/turno/bombas/operacion',
      { body: { bomba, estado } },
    ),

  historial: (fecha: string, signal?: AbortSignal): Promise<HistorialTurnosRespuesta> =>
    apiClient.get<HistorialTurnosRespuesta>(`/api/turno/historial${CADENA({ date: fecha })}`, {
      signal,
    }),

  usuarios: (signal?: AbortSignal): Promise<{ usuarios: UsuarioDelTurno[] }> =>
    apiClient.get<{ usuarios: UsuarioDelTurno[] }>('/api/turno/companeros-disponibles', { signal }),

  abrir: (cuerpo: {
    dinero_llegada: number;
    contometros?: { maquina_id: number; lectura: number }[];
  }): Promise<{
    turno_id: number;
    hora_llegada: string;
    /** Surtidores que se marcaron para el operador desde su programación. */
    lados: number[];
    /** Surtidores programados que ya tenía otro operador (no se tocan). */
    lados_ocupados: number[];
    lados_programados: number[];
    mensaje: string;
  }> =>
    apiClient.post<{
      turno_id: number;
      hora_llegada: string;
      lados: number[];
      lados_ocupados: number[];
      lados_programados: number[];
      mensaje: string;
    }>('/api/turno/abrir', {
      body: cuerpo,
    }),

  cerrar: (cuerpo: {
    total_entregado: number;
    contometros?: { maquina_id: number; lectura: number }[];
  }): Promise<BoleteoYCierreRespuesta> =>
    apiClient.post<BoleteoYCierreRespuesta>('/api/turno/cerrar', {
      body: cuerpo,
      // El cierre emite antes los comprobantes pendientes en el servicio POS: tarda más
      // que una escritura normal (el timeout por defecto son 20 s).
      timeout: CIERRE_TIMEOUT_MS,
    }),

  /**
   * Emite los comprobantes de las **transacciones libres** del turno abierto con el
   * boleteo automático. El cierre ya lo intenta solo; esto es el reintento manual.
   */
  boletear: (): Promise<BoleteoRespuesta> =>
    apiClient.post<BoleteoRespuesta>('/api/turno/boletear', { timeout: CIERRE_TIMEOUT_MS }),

  agregarCompanero: (usuarioId: number): Promise<{ id: number; mensaje: string }> =>
    apiClient.post<{ id: number; mensaje: string }>('/api/turno/companero', {
      body: { usuario_id: usuarioId },
    }),

  documentos: (filtros: FiltrosListado, signal?: AbortSignal) =>
    apiClient.get<PaginaDelTurno<DocumentoDelTurno>>(
      `/api/turno/documentos${CADENA({ ...filtros })}`,
      { signal },
    ),

  notas: (filtros: FiltrosListado, signal?: AbortSignal) =>
    apiClient.get<PaginaDelTurno<DocumentoDelTurno>>(`/api/turno/notas${CADENA({ ...filtros })}`, {
      signal,
    }),

  creditos: (filtros: FiltrosListado, signal?: AbortSignal) =>
    apiClient.get<PaginaDelTurno<CreditoDelTurno>>(
      `/api/turno/creditos${CADENA({ ...filtros })}`,
      { signal },
    ),

  posCatalogos: (
    buscar: string,
    clienteId?: number,
    tipoDocumento?: string,
    placa?: string,
    signal?: AbortSignal,
  ) =>
    apiClient.get<PosCatalogosRespuesta>(
      `/api/turno/pos/catalogos${CADENA({
        buscar,
        cliente_id: clienteId,
        tipo_documento: tipoDocumento,
        placa,
      })}`,
      {
      signal,
      },
    ),

  /** Ubica las líneas de crédito por placa o RUC (no se listan todas). */
  buscarLineasCredito: (filtros: { placa?: string; ruc?: string }) =>
    apiClient.get<LineasCreditoRespuesta>(
      `/api/turno/pos/creditos${CADENA({ placa: filtros.placa, ruc: filtros.ruc })}`,
    ),

  /** Consulta el nombre del conductor por DNI (RENIEC). No escribe nada. */
  consultarDni: (numero: string) =>
    apiClient.get<{ documento: DocumentoConsultado }>(
      `/api/turno/pos/consulta-dni${CADENA({ numero })}`,
    ),

  crearComprobante: (cuerpo: PosComprobanteSolicitud) =>
    apiClient.post<PosComprobanteRespuesta>('/api/turno/pos/comprobantes', { body: cuerpo }),

  validarCodigoCalibracion: (codigo: string) =>
    apiClient.get<ValidacionCodigo>('/api/calibration_code_lookup', { query: { codigo } }),

  registrarConsumoInterno: (
    transaccionId: number,
    cuerpo: PosConsumoInternoSolicitud,
  ) =>
    apiClient.post<PosConsumoInternoRespuesta>(
      `/api/turno/pos/transacciones/${transaccionId}/consumo-interno`,
      { body: cuerpo },
    ),

  registrarCalibracion: (transaccionId: number, codigo: string) =>
    apiClient.post<PosCalibracionRespuesta>(
      `/api/turno/pos/transacciones/${transaccionId}/calibracion`,
      { body: { codigo } },
    ),

  actualizarDireccionCliente: (clienteId: number, direccion: string) =>
    apiClient.patch<{ cliente_id: number; direccion: string; mensaje: string }>(
      `/api/turno/pos/clientes/${clienteId}/direccion`,
      { body: { direccion } },
    ),

  crearVentaCredito: (cuerpo: PosVentaCreditoSolicitud) =>
    apiClient.post<PosVentaCreditoRespuesta>('/api/turno/pos/creditos', { body: cuerpo }),

  /** PDF del ticket del turno (el operador lo imprime con el cliente local). */
  ticket: (turnoId: number): string => `/api/turno/ticket${CADENA({ turno_id: turnoId })}`,

  /** PDF del comprobante de una venta del turno (ticket 80 mm o A4). */
  ticketDocumento: (ventaId: number, formato: 'ticket' | 'a4' = 'ticket'): string =>
    `/api/turno/documentos/${ventaId}/ticket${CADENA({ formato })}`,

  /** PDF de la nota de venta. */
  ticketNota: (notaId: number): string => `/api/turno/notas/${notaId}/ticket`,

  /** PDF del vale a crédito. */
  ticketCredito: (creditoVentaId: number): string => `/api/turno/creditos/${creditoVentaId}/ticket`,

  /** PDF del ticket de un consumo interno (formato del panel anterior). */
  ticketConsumoInterno: (consumoId: number): string =>
    `/api/turno/consumo-interno/${consumoId}/ticket`,

  /** PDF del ticket de una calibración (formato del panel anterior). */
  ticketCalibracion: (calibracionId: number): string =>
    `/api/turno/calibracion/${calibracionId}/ticket`,
};

/** Turno abierto del operador con su cuadre. */
export function useTurnoActual() {
  return useQuery({
    queryKey: turnoKeys.actual,
    queryFn: ({ signal }) => turnoApi.actual(signal),
    staleTime: 15_000,
  });
}

/** Estado vivo de las bombas PTS asignadas al grifero, refrescado cada 3 s. */
export function useEstadoBombasTurno(enabled = true) {
  return useQuery({
    queryKey: turnoKeys.bombasEstado,
    queryFn: ({ signal }) => turnoApi.estadoBombas(signal),
    enabled,
    refetchInterval: 3_000,
    staleTime: 0,
  });
}

/** Activa o suspende una bomba asignada al operador. */
export function useCambiarOperacionBomba() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ bomba, estado }: { bomba: number; estado: 'On' | 'Off' }) =>
      turnoApi.cambiarOperacionBomba(bomba, estado),
    onSuccess: () => cliente.invalidateQueries({ queryKey: turnoKeys.bombasEstado }),
  });
}

export function useValidarCodigoCalibracion() {
  return useMutation({
    mutationFn: (codigo: string) => turnoApi.validarCodigoCalibracion(codigo),
  });
}

export function useRegistrarConsumoInterno() {
  const cliente = useQueryClient();
  const refrescar = () => cliente.invalidateQueries({ queryKey: ['turno', 'pos', 'catalogos'] });

  return useMutation({
    mutationFn: ({
      transaccionId,
      cuerpo,
    }: {
      transaccionId: number;
      cuerpo: PosConsumoInternoSolicitud;
    }) => turnoApi.registrarConsumoInterno(transaccionId, cuerpo),
    onSuccess: refrescar,
    // Si otra pestaña ya usó la transacción, la lista se actualiza para reflejarlo.
    onError: refrescar,
  });
}

export function useRegistrarCalibracion() {
  const cliente = useQueryClient();
  const refrescar = () => cliente.invalidateQueries({ queryKey: ['turno', 'pos', 'catalogos'] });

  return useMutation({
    mutationFn: ({ transaccionId, codigo }: { transaccionId: number; codigo: string }) =>
      turnoApi.registrarCalibracion(transaccionId, codigo),
    onSuccess: refrescar,
    onError: refrescar,
  });
}

/** Mis turnos de una fecha (para el historial). */
export function useHistorialTurnos(fecha: string, enabled = true) {
  return useQuery({
    queryKey: turnoKeys.historial(fecha),
    queryFn: ({ signal }) => turnoApi.historial(fecha, signal),
    enabled: enabled && fecha !== '',
    staleTime: 30_000,
  });
}

/** Usuarios que se pueden sumar al turno. */
export function useUsuariosDelTurno(enabled = true) {
  return useQuery({
    queryKey: turnoKeys.usuarios,
    queryFn: ({ signal }) => turnoApi.usuarios(signal),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Documentos (boletas y facturas) del turno abierto. */
export function useDocumentosDelTurno(filtros: FiltrosListado) {
  return useQuery({
    queryKey: turnoKeys.documentos(filtros),
    queryFn: ({ signal }) => turnoApi.documentos(filtros, signal),
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });
}

/** Notas de venta del turno abierto. */
export function useNotasDelTurno(filtros: FiltrosListado) {
  return useQuery({
    queryKey: turnoKeys.notas(filtros),
    queryFn: ({ signal }) => turnoApi.notas(filtros, signal),
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });
}

/** Vales a crédito del turno abierto. */
export function useCreditosDelTurno(filtros: FiltrosListado) {
  return useQuery({
    queryKey: turnoKeys.creditos(filtros),
    queryFn: ({ signal }) => turnoApi.creditos(filtros, signal),
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });
}

/** Acciones del turno (abrir, cerrar, boletear y sumar compañeros). */
export function useMutarTurno() {
  const cliente = useQueryClient();
  /** Al abrir, cerrar o boletear cambia el turno entero: se refresca todo el módulo. */
  const refrescar = () => cliente.invalidateQueries({ queryKey: turnoKeys.all });

  return {
    abrir: useMutation({ mutationFn: turnoApi.abrir, onSuccess: refrescar }),
    cerrar: useMutation({ mutationFn: turnoApi.cerrar, onSuccess: refrescar }),
    boletear: useMutation({ mutationFn: turnoApi.boletear, onSuccess: refrescar }),
    agregarCompanero: useMutation({ mutationFn: turnoApi.agregarCompanero, onSuccess: refrescar }),
  };
}

/** Catálogos filtrados por los recursos y las transacciones del operador. */
export function useCatalogosPos(
  buscar: string,
  enabled: boolean,
  clienteId?: number,
  tipoDocumento?: string,
  placa?: string,
) {
  return useQuery({
    queryKey: turnoKeys.posCatalogos(buscar, clienteId, tipoDocumento, placa),
    queryFn: ({ signal }) => turnoApi.posCatalogos(buscar, clienteId, tipoDocumento, placa, signal),
    enabled,
    staleTime: 10_000,
  });
}

/** Ubica las líneas de crédito por placa o RUC (mutación explícita, no lista todo). */
export function useBuscarLineasCredito() {
  return useMutation({
    mutationFn: (filtros: { placa?: string; ruc?: string }) =>
      turnoApi.buscarLineasCredito(filtros),
  });
}

/** Consulta el nombre del conductor por DNI (RENIEC). */
export function useConsultarDni() {
  return useMutation({ mutationFn: (numero: string) => turnoApi.consultarDni(numero) });
}

/** Crea un comprobante POS y actualiza los listados/cuadre del turno. */
export function useCrearComprobantePos() {
  const cliente = useQueryClient();
  const refrescar = () => cliente.invalidateQueries({ queryKey: turnoKeys.all });

  return useMutation({
    mutationFn: turnoApi.crearComprobante,
    onSuccess: refrescar,
    // Si la transacción la usó otra pestaña, al refrescar desaparece de la lista de
    // pendientes y el operador ya no la puede volver a seleccionar.
    onError: refrescar,
  });
}

/** Registra un vale a crédito y refresca cuadre y listados del turno. */
export function useCrearVentaCredito() {
  const cliente = useQueryClient();
  const refrescar = () => cliente.invalidateQueries({ queryKey: turnoKeys.all });

  return useMutation({
    mutationFn: turnoApi.crearVentaCredito,
    onSuccess: refrescar,
    onError: refrescar,
  });
}

/** Actualiza la dirección del cliente desde el POS y refresca sus catálogos. */
export function useActualizarDireccionClientePos() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ clienteId, direccion }: { clienteId: number; direccion: string }) =>
      turnoApi.actualizarDireccionCliente(clienteId, direccion),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ['turno', 'pos', 'catalogos'] }),
  });
}
